/* Small, self-contained Bingo sounds. No audio is queued before browser permission. */
(() => {
  'use strict';
  if (window.EvilkoBingoAudio) return;

  const PREFERENCE_KEY = 'evilko:bingo:sound-muted:v1';
  const MAX_VOICES = 8;
  const COOLDOWN = {call: 180, mark: 65, start: 600, win: 1000};
  // Frequency, delay, duration, relative level, waveform.
  const SOUNDS = {
    call: [[659.25, 0, 0.12, 0.5, 'sine'], [880, 0.105, 0.22, 0.38, 'sine']],
    mark: [[523.25, 0, 0.075, 0.32, 'triangle'], [783.99, 0.035, 0.085, 0.22, 'sine']],
    start: [[392, 0, 0.14, 0.4, 'triangle'], [523.25, 0.12, 0.14, 0.4, 'triangle'], [783.99, 0.24, 0.25, 0.4, 'sine']],
    win: [[523.25, 0, 0.18, 0.38, 'triangle'], [659.25, 0.15, 0.18, 0.38, 'triangle'], [783.99, 0.30, 0.20, 0.38, 'triangle'], [1046.5, 0.47, 0.48, 0.4, 'sine'], [659.25, 0.47, 0.44, 0.18, 'sine']]
  };

  class EvilkoBingoAudio {
    constructor({onStateChange} = {}) {
      this._Ctor = window.AudioContext || window.webkitAudioContext || null;
      this._context = null;
      this._master = null;
      this._destroyed = false;
      this._unlocked = false;
      this._activated = false;
      this._resuming = null;
      this._voices = new Set();
      this._lastPlayed = Object.create(null);
      this._callback = typeof onStateChange === 'function' ? onStateChange : null;
      this._muted = false;
      try { this._muted = window.localStorage.getItem(PREFERENCE_KEY) === '1'; } catch (_) {}
      this._visibility = () => {
        if (document.hidden) {
          this._stopAll();
          const context = this._context;
          if (context && context.state === 'running') {
            try { Promise.resolve(context.suspend()).catch(() => {}); } catch (_) {}
          }
          this._notify();
        } else if (this._unlocked && !this._muted) {
          this._resume();
        } else {
          this._notify();
        }
      };
      this._storage = event => {
        if (event.key === PREFERENCE_KEY || event.key === null) this._setMuted(event.key === null ? false : event.newValue === '1', false);
      };
      this._stateChange = () => {
        if (this._destroyed) return;
        if (this._context && this._context.state === 'running' && this._activated) this._unlocked = true;
        else this._stopAll();
        this._notify();
      };
      document.addEventListener('visibilitychange', this._visibility);
      window.addEventListener('storage', this._storage);
    }

    get available() { return !this._destroyed && !!this._Ctor; }
    get muted() { return this._muted; }
    get enabled() { return this.available && !this._muted && this._unlocked && !document.hidden && !!this._context && this._context.state === 'running'; }

    // Call directly inside a trusted click, tap, or key handler, before any await.
    unlock() {
      if (!this.available || this._muted || document.hidden) return Promise.resolve(false);
      this._activated = true;
      if (!this._context || this._context.state === 'closed') {
        this._releaseContext();
        try {
          this._context = new this._Ctor({latencyHint: 'interactive'});
          this._master = this._context.createGain();
          this._master.gain.value = 0.18;
          this._master.connect(this._context.destination);
          this._context.addEventListener('statechange', this._stateChange);
        } catch (_) {
          this._releaseContext();
          this._notify();
          return Promise.resolve(false);
        }
      }
      return this._resume();
    }

    _resume() {
      const context = this._context;
      if (this._destroyed || !context || context.state === 'closed' || this._muted || document.hidden) {
        this._notify();
        return Promise.resolve(false);
      }
      if (context.state === 'running') {
        this._unlocked = this._activated;
        this._notify();
        return Promise.resolve(this.enabled);
      }
      if (this._resuming) return this._resuming;
      let resume;
      try { resume = context.resume(); } catch (_) {
        this._notify();
        return Promise.resolve(false);
      }
      const pending = Promise.resolve(resume).then(() => {
        if (!this._destroyed && this._context === context && context.state === 'running') this._unlocked = this._activated;
        this._notify();
        return this.enabled;
      }, () => {
        this._notify();
        return false;
      }).finally(() => { if (this._resuming === pending) this._resuming = null; });
      this._resuming = pending;
      return pending;
    }

    setMuted(muted) { this._setMuted(!!muted, true); }

    _setMuted(muted, persist) {
      if (this._destroyed) return;
      this._muted = muted;
      if (persist) {
        try { window.localStorage.setItem(PREFERENCE_KEY, muted ? '1' : '0'); } catch (_) {}
      }
      if (muted) this._stopAll();
      if (this._master) this._master.gain.value = muted ? 0 : 0.18;
      this._notify();
    }

    play(kind) {
      if (!Object.prototype.hasOwnProperty.call(SOUNDS, kind)) return false;
      if (!this.enabled) {
        // Recover from a browser interruption for future events, never replay this one.
        if (this._unlocked && !this._destroyed && !this._muted && !document.hidden) this._resume();
        return false;
      }
      const now = window.performance.now();
      if (this._lastPlayed[kind] !== undefined && now - this._lastPlayed[kind] < COOLDOWN[kind]) return false;
      const notes = SOUNDS[kind];
      if (kind === 'win') this._stopAll();
      if (this._voices.size + notes.length > MAX_VOICES) return false;
      this._lastPlayed[kind] = now;
      const start = this._context.currentTime + 0.006;
      try {
        notes.forEach(note => this._note(note, start));
        return true;
      } catch (_) {
        this._stopAll();
        return false;
      }
    }

    _note([frequency, delay, duration, level, type], time) {
      const context = this._context;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = time + delay;
      const end = start + duration;
      const voice = {oscillator, gain, timer: null, stopped: false};
      this._voices.add(voice);
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, start);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(level, start + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.001, end);
      gain.gain.setValueAtTime(0, end + 0.008);
      oscillator.connect(gain);
      gain.connect(this._master);
      oscillator.onended = () => this._disposeVoice(voice);
      oscillator.start(start);
      oscillator.stop(end + 0.014);
      // A fallback also frees nodes if a browser omits the ended event after suspension.
      voice.timer = window.setTimeout(() => this._disposeVoice(voice), Math.ceil((delay + duration + 0.2) * 1000));
    }

    _disposeVoice(voice) {
      if (voice.stopped) return;
      voice.stopped = true;
      if (voice.timer !== null) window.clearTimeout(voice.timer);
      voice.oscillator.onended = null;
      try { voice.oscillator.stop(); } catch (_) {}
      try { voice.oscillator.disconnect(); } catch (_) {}
      try { voice.gain.disconnect(); } catch (_) {}
      this._voices.delete(voice);
    }

    _stopAll() { Array.from(this._voices).forEach(voice => this._disposeVoice(voice)); }

    _notify() {
      if (this._destroyed || !this._callback) return;
      try { this._callback({muted: this.muted, enabled: this.enabled, available: this.available}); } catch (_) {}
    }

    _releaseContext() {
      this._stopAll();
      const context = this._context;
      this._context = null;
      this._unlocked = false;
      this._resuming = null;
      if (this._master) {
        try { this._master.disconnect(); } catch (_) {}
        this._master = null;
      }
      if (context) {
        context.removeEventListener('statechange', this._stateChange);
        if (context.state !== 'closed') {
          try { Promise.resolve(context.close()).catch(() => {}); } catch (_) {}
        }
      }
    }

    destroy() {
      if (this._destroyed) return;
      this._destroyed = true;
      document.removeEventListener('visibilitychange', this._visibility);
      window.removeEventListener('storage', this._storage);
      this._releaseContext();
      this._callback = null;
    }
  }

  window.EvilkoBingoAudio = EvilkoBingoAudio;
})();
